import { Injectable, inject } from '@angular/core';

import { RequestService } from '@df/api/request/request.service';
import { Customer } from '@df/session/api/customer';

import { HashStorageService } from '@df/browser/storage/hash-storage.service';
import { Events } from '@df/core/service/event';
import { Status } from '@df/core/status';
import { Nexus } from '@tomandco/nexus-api-types';
import { NMyOutfits } from '../../../../../../account/pwa/my-outfits/my-outfits.interfaces';
import { LocalCustomerModel } from '../../../../../../customer/customer.model.decorator';
import { UserImagesService, UserImageRole } from '../../../../../../customer/user-images.service';
import { NBuilderConfig } from '../builder.config';

@Injectable({
    providedIn: 'root'
})
export class OutfitSaveService {
    private customer = inject(Customer);
    private requestService = inject(RequestService);
    private hashStorageService = inject(HashStorageService);
    private events = inject(Events);
    private userImagesService = inject(UserImagesService);

    /**
     * Capture gallery element as 400px image and convert to File
     * - Draws images using crossOrigin='anonymous' clones to avoid canvas tainting (CORS)
     */
    async captureGalleryImage(galleryElement: HTMLElement, background: string = 'transparent'): Promise<File | undefined> {
        try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error('Could not get canvas context');

            const SIZE = 500;
            canvas.width = SIZE;
            canvas.height = SIZE;
            ctx.imageSmoothingQuality = 'high';

            // Use inner _images-container if present for exact capture
            const container = (galleryElement.querySelector('._images-container') as HTMLElement) || galleryElement;
            const containerRect = container.getBoundingClientRect();
            const scale = Math.min(SIZE / containerRect.width, SIZE / containerRect.height);

            // Background (F6F5F5 by default) or transparent
            if (background !== 'transparent') {
                ctx.fillStyle = background;
                ctx.fillRect(0, 0, SIZE, SIZE);
            }

            // Gather image metadata from DOM (position/size) but load via CORS-safe clones
            const imgElements = Array.from(container.querySelectorAll('img')) as HTMLImageElement[];

            const drawOne = async (el: HTMLImageElement): Promise<void> => {
                // Skip elements that are not visible
                const cs = getComputedStyle(el);
                if (
                    el.classList.contains('hidden') ||
                    cs.display === 'none' ||
                    cs.visibility === 'hidden' ||
                    cs.opacity === '0' ||
                    el.offsetWidth === 0 ||
                    el.offsetHeight === 0
                )
                    return;
                // Skip not-ready images
                const src = el.currentSrc || el.src;
                if (!src) return;

                // Compute target rect relative to gallery
                const imgRect = el.getBoundingClientRect();
                const relativeX = (imgRect.left - containerRect.left) * scale;
                const relativeY = (imgRect.top - containerRect.top) * scale;
                const scaledWidth = imgRect.width * scale;
                const scaledHeight = imgRect.height * scale;

                // Center content if container does not fill canvas
                const offsetX = (SIZE - containerRect.width * scale) / 2;
                const offsetY = (SIZE - containerRect.height * scale) / 2;

                // Load via CORS-enabled clone to avoid tainted canvas
                const cloned = await new Promise<HTMLImageElement>((resolve, reject) => {
                    const image = new Image();
                    image.crossOrigin = 'anonymous';
                    image.onload = () => resolve(image);
                    image.onerror = reject;
                    image.src = src;
                });

                ctx.drawImage(cloned, relativeX + offsetX, relativeY + offsetY, scaledWidth, scaledHeight);
            };

            // Draw all images in sequence (order matters for layering)
            for (const el of imgElements) {
                try {
                    await drawOne(el);
                } catch (e) {
                    // Continue drawing others even if one fails due to CORS
                    console.warn('Skipping image during outfit capture (likely CORS):', e);
                }
            }

            // Convert to PNG Blob -> File
            const mimeType = 'image/png';
            const fileExtension = 'png';

            return await new Promise<File>((resolve, reject) => {
                canvas.toBlob(blob => {
                    if (!blob) {
                        reject(new Error('Failed to create image blob (canvas may be tainted)'));
                        return;
                    }
                    const file = new File([blob], `outfit-${Date.now()}.${fileExtension}`, { type: mimeType });
                    resolve(file);
                }, mimeType);
            });
        } catch (error) {
            console.error('Error capturing gallery image:', error);
            return undefined;
        }
    }

    /**
     * Save current outfit configuration to customer data
     * @param storageData - The outfit storage data
     * @param status - Status object for tracking operation
     * @param galleryElement - Optional gallery element to capture as image
     * @returns Promise with the saved outfit data
     */
    async saveOutfit(storageData: NBuilderConfig.IStorageData, status: Status, galleryElement?: HTMLElement): Promise<void> {
        const customerModel = this.customer.model as LocalCustomerModel;
        const savedOutfitsData = customerModel.savedOutfits;
        let savedOutfit: NMyOutfits.ISavedOutfit;
        let outfitImage: File | undefined;

        if (!customerModel.loyaltyId) {
            return;
        }

        // Capture gallery image if element is provided
        if (galleryElement) {
            outfitImage = await this.captureGalleryImage(galleryElement);
        }

        if (storageData.savedOutfitId) {
            // Update existing outfit
            const existingOutfitIndex = savedOutfitsData.findIndex(
                (outfit: NMyOutfits.ISavedOutfit) => outfit.id === storageData.savedOutfitId
            );
            if (existingOutfitIndex === -1) {
                status.error = 'Outfit not found';
                return;
            }

            savedOutfit = {
                ...savedOutfitsData[existingOutfitIndex],
                outfitData: this.hashStorageService.getHash(),
                dateUpdated: new Date().toISOString()
            };

            savedOutfitsData[existingOutfitIndex] = savedOutfit;
        } else {
            // Create new outfit
            if (savedOutfitsData.length >= NMyOutfits.MAX_SAVED_OUTFITS) {
                status.error = `Maximum of ${NMyOutfits.MAX_SAVED_OUTFITS} outfits allowed`;
                return;
            }

            // Generate a new ID by finding the highest existing ID and adding 1
            const nextId = savedOutfitsData.length > 0 ? Math.max(...savedOutfitsData.map(outfit => outfit.id)) + 1 : 1;
            storageData.savedOutfitId = nextId;
            this.events.broadcast(NBuilderConfig.updateStorageEvent, storageData);
            savedOutfit = {
                id: nextId,
                dateCreated: new Date().toISOString(),
                dateUpdated: new Date().toISOString(),
                outfitData: this.hashStorageService.getHash()
            };

            savedOutfitsData.push(savedOutfit);
        }

        if (outfitImage && savedOutfit) {
            await this.userImagesService.saveUserImage(customerModel.loyaltyId, UserImageRole.Outfit, outfitImage, status, savedOutfit.id);
        }
        // Save to customer data
        await this.requestService.submitForm(undefined, status, {
            path: Nexus.Api.Customer.Save.path,
            data: {
                saved_outfits: JSON.stringify({ outfits: savedOutfitsData })
            }
        });
    }

    /**
     * Check if current outfit hash matches any saved outfit
     * @param outfitHash - The base64 encoded outfit configuration hash
     * @returns The matching saved outfit or null
     */
    async findExistingOutfit(outfitHash: string): Promise<NMyOutfits.ISavedOutfit | null> {
        const customerModel = this.customer.model as LocalCustomerModel;

        if (!customerModel.data.saved_outfits) {
            return null;
        }

        try {
            const savedOutfitsData: NMyOutfits.ISavedOutfitsData = JSON.parse(customerModel.data.saved_outfits);
            return savedOutfitsData.outfits.find((outfit: NMyOutfits.ISavedOutfit) => outfit.outfitData === outfitHash) || null;
        } catch (error) {
            console.error('Error parsing saved outfits:', error);
            return null;
        }
    }
}
